import {
  faRightToBracket,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";

import { DisplayDateFromNow, DisplayDateTime } from "src/base-components/Date";
import { Pill } from "src/base-components/Pill";
import { TableComp as Table } from "src/base-components/Table";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { SessionOut, UserSessions } from "src/clients/taktile-api";
import { EmptyState } from "src/design-system/EmptyState";
import { Tooltip } from "src/design-system/Tooltip";
import { CellWrapper, ColumnHeader } from "src/jobs/common/TableElements";

const SESSION_TYPE_LABELS: Record<string, string> = {
  AC: "OAuth Login",
  AK: "API Key",
};

type SessionRow = SessionOut & {
  username: string;
  email: string | null;
};

const flattenSessions = (users: UserSessions[]): SessionRow[] =>
  users.flatMap((user) =>
    user.sessions.map((session) => ({
      ...session,
      username: user.username,
      email: user.email ?? null,
    })),
  );

const helper = createColumnHelper<SessionRow>();

const columns = [
  helper.accessor("username", {
    header: () => <ColumnHeader>User</ColumnHeader>,
    cell: (info) => {
      const { email, username } = info.row.original;
      const showEmail = email && email !== username;
      return (
        <CellWrapper>
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-inter-medium-12px text-gray-800">
              {username}
            </span>
            {showEmail && (
              <span className="truncate font-inter-normal-11px text-gray-500">
                {email}
              </span>
            )}
          </div>
        </CellWrapper>
      );
    },
  }),
  helper.accessor("session_type", {
    header: () => <ColumnHeader>Type</ColumnHeader>,
    cell: (info) => (
      <CellWrapper>
        <Pill
          size="sm"
          variant={info.getValue() === "AC" ? "indigo-light" : "red"}
        >
          <Pill.Text>
            {SESSION_TYPE_LABELS[info.getValue()] ?? info.getValue()}
          </Pill.Text>
        </Pill>
      </CellWrapper>
    ),
  }),
  helper.accessor("nonce", {
    header: () => <ColumnHeader>Refreshes</ColumnHeader>,
    cell: (info) => (
      <CellWrapper>
        <span className="tabular-nums">{info.getValue()}</span>
      </CellWrapper>
    ),
  }),
  helper.accessor("created_at", {
    header: () => <ColumnHeader>Created</ColumnHeader>,
    cell: (info) => (
      <CellWrapper>
        <Tooltip
          placement="top"
          title={<DisplayDateTime value={info.getValue()} />}
        >
          <DisplayDateFromNow value={info.getValue()} />
        </Tooltip>
      </CellWrapper>
    ),
  }),
  helper.accessor("updated_at", {
    header: () => <ColumnHeader>Last activity</ColumnHeader>,
    cell: (info) => (
      <CellWrapper>
        <Tooltip
          placement="top"
          title={<DisplayDateTime value={info.getValue()} />}
        >
          <DisplayDateFromNow value={info.getValue()} />
        </Tooltip>
      </CellWrapper>
    ),
  }),
  helper.accessor("id", {
    header: () => <ColumnHeader>Session ID</ColumnHeader>,
    cell: (info) => (
      <CellWrapper>
        <TruncatedIdPill dataLoc="session-id-pill" id={info.getValue()} />
      </CellWrapper>
    ),
  }),
];

type Props = {
  data: UserSessions[];
  includeExpired: boolean;
  isError: boolean;
  isLoading: boolean;
};

export const SessionsTable: React.FC<Props> = ({
  data,
  includeExpired,
  isError,
  isLoading,
}) => {
  if (isError) {
    return (
      <EmptyState
        dataLoc="sessions-error-state"
        description="An error occurred while loading sessions. Please try again."
        headline="Failed to load sessions"
        icon={faTriangleExclamation}
        variant="error"
      />
    );
  }

  const rows = flattenSessions(data);

  if (!isLoading && rows.length === 0) {
    return (
      <EmptyState
        dataLoc="sessions-empty-state"
        description={`No ${
          includeExpired ? "" : "active "
        }sessions found for this organization.`}
        headline="No sessions"
        icon={faRightToBracket}
      />
    );
  }

  return (
    <Table
      columns={columns}
      data={rows}
      dataLoc="sessions-table"
      disableVirtualization
      frameClassName="w-full"
      getRowId={(row) => row.id}
      isLoading={isLoading}
      loadingRowsCount={10}
    />
  );
};
